/**
 * El inventario en la caja y el aviso «según el inventario no alcanza».
 *
 * La caja guarda las existencias de su bodega (colección `stock`) y las recetas (`materials`,
 * `variant_materials`). Se bajan al abrir el POS y con «Sincronizar», y cada venta devuelve las de
 * lo vendido ya descontadas (`App\Domains\Pos\Services\Legacy\ExistenciasDeLaCajaService`). Si la
 * sucursal tiene varias cajas, además pregunta cada 5 minutos qué cambió (plugins/pos/catalogo-al-dia.js);
 * con una sola, las compras se ven al volver a abrir o al sincronizar.
 *
 * Al elegir un producto, `faltantes()` suma lo que pide todo el carrito más lo nuevo —el producto,
 * los componentes del combo o los ingredientes de la receta— y dice qué no alcanza. Es un aviso: la
 * persona revisa en físico y puede vender igual (hay inventarios desordenados).
 */
const CLAVE_FECHA = 'pos_existencias_fecha';
// Lo que el servidor pide que la caja mande la próxima vez (`desde`) y cada cuántos segundos (0 = no).
const CLAVE_HASTA = 'pos_existencias_hasta';
const CLAVE_CADA = 'pos_existencias_cada';
const NIVELES = 5;

const INDICES = {
  stock: ['inv_product_id'],
  materials: ['inv_product_id'],
  variant_materials: ['inv_product_variant_id'],
};

/** Las colecciones nuevas no existen en una caja instalada antes de este cambio. */
export function asegurarColecciones(loki) {
  for (const nombre of Object.keys(INDICES)) {
    if (!loki.getCollection(nombre)) {
      loki.addCollection(nombre, { indices: INDICES[nombre] });
    }
  }
}

/**
 * Aplica lo que manda el servidor: todas las existencias de la bodega (al abrir, al sincronizar)
 * o sólo las de lo vendido (`parcial`: se reemplazan esas, y el producto sin fila quedó en cero).
 */
export function aplicarExistencias(db, existencias) {
  if (!existencias || !Array.isArray(existencias.filas)) {
    return;
  }
  const loki = db.adapter.db;
  asegurarColecciones(loki);
  const stock = loki.getCollection('stock');
  if (existencias.parcial) {
    const productos = existencias.productos || [];
    stock.findAndRemove({ inv_product_id: { $in: productos } });
  } else {
    stock.findAndRemove({});
    localStorage.setItem(CLAVE_FECHA, new Date().toISOString());
  }
  if (existencias.filas.length) {
    stock.insert(existencias.filas.map((f) => ({ ...f })));
  }
  // Sólo lo traen las consultas de toda la bodega o de lo que cambió, no las de una venta.
  if (existencias.hasta) {
    localStorage.setItem(CLAVE_HASTA, existencias.hasta);
    localStorage.setItem(CLAVE_CADA, String(parseInt(existencias.cada, 10) || 0));
  }
}

/** Para preguntar qué cambió: `{ desde, cada }` (cada en segundos, 0 = no preguntar). */
export function revisionDeExistencias() {
  return {
    desde: localStorage.getItem(CLAVE_HASTA) || '',
    cada: parseInt(localStorage.getItem(CLAVE_CADA), 10) || 0,
  };
}

function existencia(stock, productoId, varianteId) {
  const filas = stock.find({ inv_product_id: productoId });
  const deLaVariante = varianteId ? filas.filter((f) => f.inv_product_variant_id === varianteId) : filas;
  return deLaVariante.reduce((total, f) => total + (parseFloat(f.quantity) || 0), 0);
}

function seArmaAlVender(p) {
  return !!(p && p.has_bom && !p.is_manufactured);
}

/** Suma en `necesita` lo que se lleva `cantidad` de `producto`: él mismo, su combo o su receta. */
function expandir(db, producto, varianteId, cantidad, necesita, nivel = 0) {
  if (!producto || nivel > NIVELES || !cantidad) {
    return;
  }
  const loki = db.adapter.db;
  const tipo = producto.type || producto.product_type;
  if (tipo === 'kit') {
    for (const item of producto['kit-items'] || []) {
      const hijo = loki.getCollection('products').findOne({ id: item.id }) || item;
      expandir(db, hijo, null, cantidad * (parseFloat(item.quantity) || 1), necesita, nivel + 1);
    }
    return;
  }
  if (seArmaAlVender(producto)) {
    const recetas = loki.getCollection('materials');
    if (!recetas) {
      return;
    }
    const cambios = {};
    if (varianteId && loki.getCollection('variant_materials')) {
      for (const c of loki.getCollection('variant_materials').find({ inv_product_variant_id: varianteId })) {
        cambios[c.inv_product_material_id] = c.quantity;
      }
    }
    for (const r of recetas.find({ inv_product_id: producto.id })) {
      const porUnidad = parseFloat(cambios[r.id] !== undefined ? cambios[r.id] : r.quantity) || 0;
      const ingrediente = {
        id: r.inv_material_product_id,
        name: r.material_name,
        has_inventory: r.has_inventory,
        has_bom: r.has_bom,
        is_manufactured: r.is_manufactured,
        unity: r.unity ? { name: r.unity } : null,
      };
      expandir(db, ingrediente, null, cantidad * porUnidad, necesita, nivel + 1);
    }
    return;
  }
  // La línea del pedido guarda sólo una parte del producto: lo demás se lee del catálogo.
  if (producto.has_presentation === undefined) {
    producto = loki.getCollection('products').findOne({ id: producto.id }) || producto;
  }
  if (!producto.has_inventory) {
    return;
  }
  // Una presentación (la caja de 72) descuenta del producto en su unidad base: cantidad × equivalencia.
  // Sin la presentación elegida, o con un catálogo que no trae la equivalencia, no se adivina.
  if (producto.has_presentation) {
    const coleccion = db.adapter.db.getCollection('product_variants');
    const presentacion = varianteId && coleccion ? coleccion.findOne({ id: varianteId }) : null;
    const factor = presentacion ? parseFloat(presentacion.conversion_factor) : 0;
    if (!(factor > 0)) {
      return;
    }
    cantidad = cantidad * (factor > 1 ? factor : 1);
    varianteId = null;
  }
  const clave = producto.id + '|' + (varianteId || '');
  if (!necesita[clave]) {
    necesita[clave] = {
      id: producto.id,
      varianteId: varianteId || null,
      nombre: producto.name,
      unidad: producto.unity && producto.unity.name ? producto.unity.name : '',
      cantidad: 0,
    };
  }
  necesita[clave].cantidad += cantidad;
}

/**
 * Lo que no alcanza si se agrega `cantidad` de `producto` (y `varianteId`) a lo que ya lleva el
 * pedido. Sólo de lo que pide el producto nuevo. Vacío si la caja todavía no tiene existencias.
 *
 * @return {Array<{nombre: string, unidad: string, quedan: number, necesita: number}>}
 */
export function faltantes(db, producto, varianteId, cantidad, lineasDelPedido) {
  const loki = db.adapter && db.adapter.db;
  if (!loki || !localStorage.getItem(CLAVE_FECHA) || !loki.getCollection('stock')) {
    return [];
  }
  const delNuevo = {};
  expandir(db, producto, varianteId, cantidad, delNuevo);
  if (!Object.keys(delNuevo).length) {
    return [];
  }
  const total = {};
  for (const linea of lineasDelPedido || []) {
    const lineas = [linea].concat(linea.sub_items || []);
    for (const l of lineas) {
      if (l && l.product) {
        expandir(db, l.product, l.product_variant && l.product_variant.id, parseFloat(l.quantity) || 0, total);
      }
    }
  }
  expandir(db, producto, varianteId, cantidad, total);

  const stock = loki.getCollection('stock');
  const resultado = [];
  for (const clave of Object.keys(delNuevo)) {
    const n = total[clave];
    const quedan = existencia(stock, n.id, n.varianteId);
    if (quedan < n.cantidad) {
      resultado.push({ nombre: n.nombre, unidad: n.unidad, quedan, necesita: n.cantidad });
    }
  }
  return resultado;
}

/**
 * Descuenta de las existencias de la caja lo que se llevan estas líneas (con sus recetas y combos).
 * Lo usa la venta sin conexión de la app de escritorio: en línea, el servidor devuelve las existencias
 * ya descontadas. Si el producto no tenía fila, queda en negativo, como en el servidor.
 */
export function descontarDeLaCaja(db, lineas) {
  const loki = db.adapter && db.adapter.db;
  if (!loki || !localStorage.getItem(CLAVE_FECHA) || !loki.getCollection('stock')) {
    return;
  }
  const total = {};
  for (const linea of lineas || []) {
    for (const l of [linea].concat(linea.sub_items || [])) {
      if (l && l.product) {
        expandir(db, l.product, l.product_variant && l.product_variant.id, parseFloat(l.quantity) || 0, total);
      }
    }
  }
  const stock = loki.getCollection('stock');
  for (const n of Object.values(total)) {
    const fila = stock.find({ inv_product_id: n.id }).find((f) => (f.inv_product_variant_id || null) === n.varianteId);
    if (fila) {
      fila.quantity = (parseFloat(fila.quantity) || 0) - n.cantidad;
      stock.update(fila);
    } else {
      stock.insert({ inv_product_id: n.id, inv_product_variant_id: n.varianteId, quantity: -n.cantidad });
    }
  }
}
